import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import CountUp from 'react-countup';
import { useInView } from 'react-intersection-observer';
import { STATS, PHONE, PHONE_DISPLAY } from '../../data/appData';

function StatCard({ label, value, prefix, suffix, icon, delay }) {
  const { ref, inView } = useInView({ triggerOnce: true });
  return (
    <div ref={ref} style={{ textAlign: 'center', padding: '20px', animation: inView ? `slideInUp 0.6s ${delay}s both` : 'none' }}>
      <div style={{ fontSize: '24px', marginBottom: '4px' }}>{icon}</div>
      <div style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 'clamp(1.8rem, 3vw, 2.5rem)', color: 'white', letterSpacing: '-0.03em', lineHeight: 1 }}>
        {prefix}{inView ? <CountUp end={value} duration={2.5} separator="," /> : '0'}{suffix}
      </div>
      <div style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.6)', marginTop: '4px', fontWeight: 500 }}>{label}</div>
    </div>
  );
}

export default function HeroSection() {
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const heroRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (!heroRef.current) return;
      const rect = heroRef.current.getBoundingClientRect();
      setMousePos({ x: (e.clientX - rect.left) / rect.width, y: (e.clientY - rect.top) / rect.height });
    };
    window.addEventListener('mousemove', handler, { passive: true });
    return () => window.removeEventListener('mousemove', handler);
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <section ref={heroRef} style={{ position: 'relative', minHeight: '100vh', display: 'flex', flexDirection: 'column', justifyContent: 'center', overflow: 'hidden', background: '#020617' }}>



      {/* ── Tam genişlik çizgi — navbar hizasında ── */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4, duration: 1.2 }}
        style={{
          position: 'absolute', top: '72px', left: 0, right: 0,
          height: '2px',
          background: 'linear-gradient(90deg, transparent 0%, #22d3ee 5%, #7fffff 50%, #22d3ee 95%, transparent 100%)',
          boxShadow: '0 0 40px 8px rgba(34,211,238,0.9)',
          pointerEvents: 'none', zIndex: 5,
        }}
      />

      {/* ── Decorative overlays ── */}
      <div style={{ position: 'absolute', inset: 0, background: `radial-gradient(ellipse 80% 80% at ${mousePos.x * 100}% ${mousePos.y * 100}%, rgba(0,180,204,0.10) 0%, transparent 60%)`, transition: 'background 0.5s', pointerEvents: 'none', zIndex: 2 }}/>
      <div style={{ position: 'absolute', inset: 0, backgroundImage: `linear-gradient(rgba(0,212,240,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(0,212,240,0.04) 1px, transparent 1px)`, backgroundSize: '60px 60px', pointerEvents: 'none', zIndex: 2 }}/>

      {/* Rotating ring */}
      <div style={{ position: 'absolute', right: '-100px', top: '10%', width: '500px', height: '500px', borderRadius: '50%', border: '1px solid rgba(0,180,204,0.15)', animation: 'spin-slow 30s linear infinite', pointerEvents: 'none', zIndex: 2 }}>
        <div style={{ position: 'absolute', top: '20px', left: '20px', right: '20px', bottom: '20px', borderRadius: '50%', border: '1px solid rgba(0,180,204,0.1)' }}/>
      </div>

      {/* Floating orbs */}
      <div style={{ position: 'absolute', top: '15%', left: '8%', width: '180px', height: '180px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(0,180,204,0.15) 0%, transparent 70%)', animation: 'blob 9s ease-in-out infinite', pointerEvents: 'none', zIndex: 2 }}/>
      <div style={{ position: 'absolute', top: '55%', left: '5%', width: '120px', height: '120px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(0,180,204,0.10) 0%, transparent 70%)', animation: 'blob 12s ease-in-out infinite 2s', pointerEvents: 'none', zIndex: 2 }}/>
      <div style={{ position: 'absolute', top: '30%', right: '12%', width: '200px', height: '200px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(10,79,126,0.2) 0%, transparent 70%)', animation: 'blob 10s ease-in-out infinite 1s', pointerEvents: 'none', zIndex: 2 }}/>

      {/* Floating particles */}
      {[...Array(6)].map((_, i) => (
        <div key={i} style={{
          position: 'absolute',
          width: `${4 + i * 2}px`, height: `${4 + i * 2}px`,
          borderRadius: '50%',
          background: i % 2 === 0 ? 'rgba(0,212,240,0.6)' : 'rgba(255,107,53,0.5)',
          top: `${15 + i * 12}%`,
          left: `${10 + i * 8}%`,
          animation: `float ${5 + i}s ease-in-out infinite ${i * 0.7}s`,
          pointerEvents: 'none',
          zIndex: 2,
          boxShadow: i % 2 === 0 ? '0 0 8px rgba(0,212,240,0.8)' : '0 0 8px rgba(255,107,53,0.6)',
        }}/>
      ))}

      {/* ── Hero content ── */}
      <div className="container" style={{ position: 'relative', zIndex: 10, paddingTop: '140px', paddingBottom: '60px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 420px', gap: '48px', alignItems: 'center' }}>

          {/* Sol — başlık & butonlar */}
          <div>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '6px 16px', borderRadius: 'var(--radius-full)', background: 'rgba(0,180,204,0.12)', border: '1px solid rgba(0,180,204,0.3)', marginBottom: '28px', animation: 'slideInUp 0.6s 0.1s both' }}>
              <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'var(--teal)', boxShadow: '0 0 8px var(--teal)' }}/>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.72rem', letterSpacing: '0.15em', color: 'var(--teal-bright)', textTransform: 'uppercase' }}>
                Ankara'nın #1 Tesisat Çözüm Merkezi
              </span>
            </div>

            <h1 style={{ fontSize: 'clamp(2.4rem, 5vw, 4.2rem)', fontFamily: 'var(--font-display)', fontWeight: 800, letterSpacing: '-0.04em', lineHeight: 1.05, color: 'white', marginBottom: '24px', animation: 'slideInUp 0.6s 0.2s both' }}>
              <span style={{
                display: 'block',
                fontSize: '0.28em',
                fontWeight: 500,
                letterSpacing: '0.35em',
                color: 'rgba(255,255,255,0.4)',
                textTransform: 'uppercase',
                marginBottom: '0.6em',
                fontFamily: 'var(--font-mono)',
              }}>
                ── TAŞGIN TESİSAT ──
              </span>
              TEKNOLOJİK<br/>
              <span style={{ color: 'var(--teal-bright)' }}>& KESİN</span><br/>
              ÇÖZÜMLER
            </h1>

            <p style={{ fontSize: '1.05rem', color: 'rgba(255,255,255,0.7)', maxWidth: '480px', lineHeight: 1.7, marginBottom: '36px', animation: 'slideInUp 0.6s 0.3s both' }}>
              Termal kamera, robotik boru kamerası ve hydrojet sistemleriyle her türlü tesisat sorununu <strong style={{ color: 'rgba(255,255,255,0.9)' }}>profesyonel ekibimiz</strong> ile çözüyoruz.
            </p>

            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', animation: 'slideInUp 0.6s 0.4s both' }}>
              <a href={`tel:${PHONE}`} style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', padding: '14px 28px', borderRadius: 'var(--radius-full)', background: 'var(--accent-orange)', color: 'white', fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: '0.95rem', cursor: 'pointer', transition: 'all 0.2s', border: 'none', textDecoration: 'none', boxShadow: '0 8px 24px rgba(255,107,53,0.4)' }}>
                🚨 Hemen Ara: {PHONE_DISPLAY}
              </a>
              <button onClick={() => scrollTo('hizmetler')} className="btn btn-ghost" style={{ fontSize: '0.95rem', padding: '14px 28px', color: 'white', borderColor: 'rgba(255,255,255,0.3)', background: 'transparent', cursor: 'pointer' }}>
                Hizmetlerimiz →
              </button>
            </div>

            <div style={{ display: 'flex', gap: '16px', marginTop: '28px', flexWrap: 'wrap', animation: 'slideInUp 0.6s 0.5s both' }}>
              {['✅ Lisanslı & Sigortalı', '⚡ 45 Dk Ulaşım', '🏆 18 Yıl Deneyim', '🔧 Hızlı Müdahale'].map(b => (
                <div key={b} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.78rem', color: 'rgba(255,255,255,0.55)', fontWeight: 500 }}>{b}</div>
              ))}
            </div>
          </div>

          {/* Sağ — Bize Ulaşın kartı */}
          <div style={{ animation: 'slideInRight 0.7s 0.4s both' }}>
            <div style={{
              background: 'rgba(255,255,255,0.04)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              border: '1px solid rgba(0,180,204,0.25)',
              borderRadius: '24px',
              padding: '36px 32px',
              boxShadow: '0 20px 60px rgba(0,0,0,0.4), 0 0 40px rgba(0,180,204,0.08)',
            }}>
              <div style={{ textAlign: 'center', marginBottom: '28px' }}>
                <div style={{ fontSize: '40px', marginBottom: '10px', animation: 'float 3s ease-in-out infinite' }}>📞</div>
                <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.4rem', fontWeight: 800, color: 'white', marginBottom: '6px' }}>Bize Ulaşın</h3>
                <p style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.55)', lineHeight: 1.5 }}>Pzt–Cmt 08:00–20:00 uzman ekibimiz tüm tesisat sorunlarınız için hazır</p>
              </div>

              {/* Telefon butonu */}
              <a href={`tel:${PHONE}`} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
                width: '100%', padding: '16px', borderRadius: '14px',
                background: 'linear-gradient(135deg, #ff6b35, #ff8c42)',
                color: 'white', fontFamily: 'var(--font-display)', fontWeight: 800,
                fontSize: '1.1rem', textDecoration: 'none',
                boxShadow: '0 8px 28px rgba(255,107,53,0.45)',
                marginBottom: '12px', transition: 'all 0.2s',
              }}>
                📞 {PHONE_DISPLAY}
              </a>

              {/* WhatsApp butonu */}
              <a href={`https://wa.me/90${PHONE.substring(1)}?text=Merhaba, randevu almak istiyorum.`}
                target="_blank" rel="noreferrer"
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
                  width: '100%', padding: '14px', borderRadius: '14px',
                  background: 'rgba(37,211,102,0.12)',
                  border: '1px solid rgba(37,211,102,0.35)',
                  color: '#25D366', fontFamily: 'var(--font-display)', fontWeight: 700,
                  fontSize: '0.95rem', textDecoration: 'none',
                  marginBottom: '20px', transition: 'all 0.2s',
                }}>
                💬 WhatsApp ile Yaz
              </a>

              {/* Bilgi satırları */}
              {[
                { icon: '📍', label: 'Güzelkent Mah. 369. Sok., Ankara' },
                { icon: '⏰', label: 'Pzt–Cmt: 08:00 – 20:00' },
                { icon: '⚡', label: '45 Dakikada Kapınızda' },
              ].map(({ icon, label }) => (
                <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 0', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                  <span style={{ fontSize: '16px', flexShrink: 0 }}>{icon}</span>
                  <span style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.55)', lineHeight: 1.4 }}>{label}</span>
                </div>
              ))}
            </div>
          </div>

        </div>
      </div>

      <style>{`
        @media (max-width: 1024px) { .hero-grid { grid-template-columns: 1fr !important; } .hero-contact-card { display: none !important; } }
      `}</style>

      {/* ── Stats bar ── */}
      <div style={{ position: 'relative', zIndex: 10, borderTop: '1px solid rgba(255,255,255,0.08)', background: 'rgba(0,0,0,0.2)', backdropFilter: 'blur(10px)', paddingBottom: '72px' }}>
        <div className="container">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)' }}>
            {STATS.map((stat, i) => (
              <StatCard key={stat.label} {...stat} delay={0.6 + i * 0.1} />
            ))}
          </div>
        </div>
      </div>

      {/* ── Wave divider ── */}
      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, zIndex: 10 }}>
        <svg viewBox="0 0 1440 80" xmlns="http://www.w3.org/2000/svg" style={{ display: 'block', width: '100%' }}>
          <path d="M0,40 C360,80 1080,0 1440,40 L1440,80 L0,80 Z" fill="var(--bg-primary)"/>
        </svg>
      </div>

      <style>{`@media (max-width: 640px) { .stats-grid { grid-template-columns: repeat(2,1fr) !important; } }`}</style>
    </section>
  );
}
